// PlayerCard — pixel-match of cards.jsx (PlayerCard) from the Design Bundle.
// Layout, colours, dimensions, hover behaviour all driven by ProfileCard.css.
//
// Differences vs. the standalone bundle component:
//   - Real photo URL takes priority over the SVG portrait. We only fall
//     back to initials when the API returns no image at all (broken-image
//     event also escalates to initials).
//   - Country flag is the SVG URL returned by country_flag_helper (the
//     same source the rest of the app uses) instead of the emoji palette
//     from the bundle. Keeps appearance consistent with PlayerHeroBanner.
//   - Club logo comes from the API (`club.logo_url`); the bundle's hard-
//     coded CLUBS palette is only used as a 2-letter monogram fallback
//     when the real logo URL is missing.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { TpMark } from "./TpMark";

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

function VerifiedBadge({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-label="Verified"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M10 1 L12.2 2.6 L14.9 2.3 L16 4.8 L18.3 6.3 L17.7 9 L18.3 11.7 L16 13.2 L14.9 15.7 L12.2 15.4 L10 17 L7.8 15.4 L5.1 15.7 L4 13.2 L1.7 11.7 L2.3 9 L1.7 6.3 L4 4.8 L5.1 2.3 L7.8 2.6 Z"
        fill="#4A90D9"
      />
      <path
        d="M6 10.2 L8.8 13 L14 7.4"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowRight({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7 H11 M7.5 3 L11 7 L7.5 11"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Avatar({ photoUrl, fallbackInitials }) {
  const [broken, setBroken] = useState(false);
  if (photoUrl && !broken) {
    return (
      <div className="tp-avatar">
        <img src={photoUrl} alt="" onError={() => setBroken(true)} />
      </div>
    );
  }
  return (
    <div className="tp-avatar">
      <div className="tp-avatar-initials">{fallbackInitials || "?"}</div>
    </div>
  );
}

function ClubBadge({ club }) {
  const [broken, setBroken] = useState(false);
  if (!club) return null;
  const initials =
    (club.name || "")
      .replace(/[^A-Za-z]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "FC";
  if (club.logo_url && !broken) {
    return (
      <img
        className="tp-club-logo"
        src={club.logo_url}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="tp-club-logo-fallback">{initials}</span>;
}

// All citizenships (L-FE-1): `nationalities` from useProfileCard (ordered, falls back to the single one).
function CountryStat({ nationality, nationalities }) {
  const list = Array.isArray(nationalities) && nationalities.length ? nationalities : nationality ? [nationality] : [];
  if (!list.length) return null;
  const names = list.map((n) => n.name).filter(Boolean).join(" / ");
  return (
    <>
      {list.map((n, i) => (n.flag_url ? <img key={n.name || i} className="tp-flag" src={n.flag_url} alt="" /> : null))}
      {names && <span className="tp-stat">{names}</span>}
    </>
  );
}

// -----------------------------------------------------------------------------
// Card
// -----------------------------------------------------------------------------

export default function PlayerCard({ data }) {
  const navigate = useNavigate();
  if (!data) return null;

  const goToProfile = () => navigate(data.profile_url);

  // Stop click from bubbling so e.g. clicking the club row in a future
  // extension can route somewhere different. For now the whole card
  // routes to the same place.
  const stop = (e) => e.stopPropagation();

  // 2-3 letter initials as a deterministic fallback when the API has no
  // photo. Matches the bundle's "initials" portrait tone.
  const fallbackInitials = (data.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  return (
    <div
      className="tp-card"
      onClick={goToProfile}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && goToProfile()}
      title={`Open ${data.name}'s profile`}
    >
      <div className="tp-card-inner">
        {/* HEAD: avatar + name/position + corner mark */}
        <div className="tp-row tp-row-head">
          <div className="tp-avatar-wrap">
            <Avatar photoUrl={data.photo_url} fallbackInitials={fallbackInitials} />
            {data.jersey_number != null && (
              <div className="tp-jersey">#{data.jersey_number}</div>
            )}
          </div>

          <div className="tp-head-text">
            <div className="tp-name-row">
              <span className="tp-name">{data.name}</span>
              {data.verified && <VerifiedBadge size={16} />}
            </div>
            <div className="tp-sub">
              {data.position && (
                <span className="tp-pos-primary">{data.position}</span>
              )}
              {data.secondary_position && (
                <>
                  <span className="tp-pos-slash">/</span>
                  <span className="tp-pos-secondary">{data.secondary_position}</span>
                </>
              )}
            </div>
          </div>

          <div className="tp-corner-mark" title="TransferPitch">
            <TpMark size={26} />
          </div>
        </div>

        {/* CLUB row */}
        {data.club && data.club.name && (
          <div className="tp-row tp-row-club" onClick={stop}>
            <ClubBadge club={data.club} />
            <span className="tp-club-name">{data.club.name}</span>
          </div>
        )}

        {/* STATS row — Nationality . Age . Height . Foot. Each segment
            renders only when its data is present, and each separator
            dot only renders when there's a previous segment, so a
            player missing one stat doesn't end the row with a
            dangling " . ". */}
        {(data.nationality || data.age != null || data.height || data.foot) && (
          <div className="tp-row tp-row-stats">
            <CountryStat nationality={data.nationality} nationalities={data.nationalities} />
            {data.age != null && (
              <>
                {data.nationality && <span className="tp-dot">·</span>}
                <span className="tp-stat">{data.age}y</span>
              </>
            )}
            {data.height && (
              <>
                {(data.nationality || data.age != null) && (
                  <span className="tp-dot">·</span>
                )}
                <span className="tp-stat">{data.height}cm</span>
              </>
            )}
            {data.foot && (
              <>
                {(data.nationality || data.age != null || data.height) && (
                  <span className="tp-dot">·</span>
                )}
                <span className="tp-stat">{data.foot} foot</span>
              </>
            )}
          </div>
        )}

        {/* MARKET VALUE row (only when present in API response). The
            small trend-up glyph signals "valuation" without needing a
            currency-specific icon; the amount string already carries
            the €/$/£ symbol resolved by formatMarketValue. */}
        {data.market_value && (
          <div className="tp-row tp-row-mv">
            <span className="tp-mv-label">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ display: "inline-block", marginRight: 6, verticalAlign: "-1px" }}
                aria-hidden="true"
              >
                <polyline points="3 17 9 11 13 15 21 7" />
                <polyline points="14 7 21 7 21 14" />
              </svg>
              Market value
            </span>
            <span className="tp-mv-amount">{data.market_value}</span>
          </div>
        )}

        {/* CTA row */}
        <div className="tp-row tp-row-cta">
          <span className="tp-cta-text">View full profile</span>
          <ArrowRight />
        </div>
      </div>
    </div>
  );
}
